// utils/websocket.js
import { io } from "socket.io-client";
import { baseUrl } from "./url";

class WebSocketService {
   constructor() {
    this.socket = null;
    this.eventHandlers = new Map();
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 0; 
    this.intentionalDisconnect = false;
    this.currentUserId = null;
    this.currentToken = null;
    this.serverUrl = baseUrl;
    this.tokenExpiryTimer = null;

    // ✅ UNIQUE TAB ID (per browser tab)
    this.tabId = sessionStorage.getItem("WS_TAB_ID") || Math.random().toString(36).slice(2);
    sessionStorage.setItem("WS_TAB_ID", this.tabId);

    this.socketOptions = {
        path: "/socket.io/",
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        autoConnect: true
    };
}

    /**
     * @param {string} token 
     * @returns {object|null} 
     */
    decodeToken(token) {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error("Error decoding token:", error);
            return null;
        }
    }

    /**
     * Check if token is expired
     * @param {string} token - JWT token
     * @returns {boolean} - True if token is expired
     */
    isTokenExpired(token) {
        if (!token) return true;

        const decoded = this.decodeToken(token);
        if (!decoded || !decoded.exp) return true;

        const currentTime = Math.floor(Date.now() / 1000);
        const isExpired = decoded.exp < currentTime;

        if (isExpired) {
            console.log("Token expired at:", new Date(decoded.exp * 1000).toLocaleString());
        }

        return isExpired;
    }

    /**
     * Get token expiration time
     * @param {string} token - JWT token
     * @returns {number|null} - Expiration timestamp in milliseconds
     */
    getTokenExpiryTime(token) {
        if (!token) return null;
        const decoded = this.decodeToken(token);
        if (decoded && decoded.exp) {
            return decoded.exp * 1000;
        }
        return null;
    }

    /**
     * Schedule token expiration handler
     */
    scheduleTokenExpiration() {
        // Clear existing timer
        if (this.tokenExpiryTimer) {
            clearTimeout(this.tokenExpiryTimer);
            this.tokenExpiryTimer = null;
        }

        if (!this.currentToken) return;

        const expiryTime = this.getTokenExpiryTime(this.currentToken);
        if (!expiryTime) return;

        const currentTime = Date.now();
        const timeUntilExpiry = expiryTime - currentTime;

        console.log(`Token expires in ${Math.floor(timeUntilExpiry / 1000)} seconds`);

        if (timeUntilExpiry <= 0) {
            console.log("Token already expired, disconnecting WebSocket");
            this.handleTokenExpired();
        } else {
            this.tokenExpiryTimer = setTimeout(() => {
                console.log("⏰ Token expiration timer triggered - disconnecting WebSocket");
                this.handleTokenExpired();
            }, timeUntilExpiry);
        }
    }

    
    handleTokenExpired() {
        console.log("🔐 Token has expired - disconnecting WebSocket");

        // Mark as intentional disconnect to prevent auto-reconnection
        this.intentionalDisconnect = true;
        this.isConnected = false;

        // Trigger token expired event for UI
        this.triggerEvent("token_expired", {
            message: "Your session has expired. Please login again.",
            timestamp: new Date().toISOString()
        });

        // Clear token and user data
        const expiredToken = this.currentToken;
        this.currentToken = null;
        this.currentUserId = null;

        // Clear the timer
        if (this.tokenExpiryTimer) {
            clearTimeout(this.tokenExpiryTimer);
            this.tokenExpiryTimer = null;
        }

        // Disconnect the socket
        if (this.socket) {
            console.log("🔌 Disconnecting WebSocket due to token expiration");
            this.socket.removeAllListeners();
            this.socket.disconnect();
            this.socket = null;
        }

        // Clear any pending reconnection attempts
        if (this.reconnectAttempts > 0) {
            this.reconnectAttempts = this.maxReconnectAttempts;
        }
    }

    /**
     * @param {string} authToken - Authentication token
     * @param {string|number} userId - User ID
     */
connect(authToken, userId = null) {
    if (!authToken) {
        console.warn("⚠️ No auth token provided");
        return Promise.reject(new Error("No authentication token provided"));
    }

    if (this.isTokenExpired(authToken)) {
        console.error("❌ Token expired");
        this.triggerEvent("token_expired", {
            message: "Token expired",
            timestamp: new Date().toISOString()
        });
        return Promise.reject(new Error("Token expired"));
    }

    if (this.socket && this.socket.connected) {
        this.socket.disconnect();
    }

    this.currentToken = authToken;
    this.currentUserId = userId;
    this.intentionalDisconnect = false;

    this.scheduleTokenExpiration();

    return new Promise((resolve, reject) => {
        try {
            console.log(`🌐 Connecting to: ${this.serverUrl}`);

            this.socket = io(this.serverUrl, {
                ...this.socketOptions
            });

            // ✅ CONNECT
            this.socket.on("connect", () => {
                console.log("✅ Connected:", this.socket.id);

                this.isConnected = true;
                this.reconnectAttempts = 0;

                // ✅ MARK THIS TAB AS ACTIVE SESSION
                localStorage.setItem("ACTIVE_WS_TAB", this.tabId);

                console.log("📨 Registering socket...");
                this.socket.emit("register", {
                    token: authToken
                });
                
                // Resolve the promise once connected and registered
                resolve(true);
            });

            
            this.socket.on("online_users_update", (data) => {
                console.log("👥 ONLINE USERS:", data);

                if (!this.currentUserId) {
                    this.currentUserId = userId;
                }

                this.triggerEvent("online_users_update", data);
            });
let hasHandledLogout = false;

const forceLogoutHandler = (data) => {
    console.log("🔥 Force logout event received");

    // 🚨 Prevent multiple executions
    if (hasHandledLogout) {
        console.log("⚠️ Logout already handled, ignoring duplicate");
        return;
    }

    hasHandledLogout = true;

    // 🚨 Ignore if already disconnected
    if (!this.socket || !this.socket.connected) {
        console.warn("Socket already disconnected, skipping");
        return;
    }

    // 🚨 Mark intentional BEFORE handling
    this.intentionalDisconnect = true;

    this.handleAuthFailure({
        ...data,
        message: data?.message || "Logged in from another device"
    }, true);
};

            this.socket.on("force_logout", forceLogoutHandler);
            this.socket.on("duplicate_login", forceLogoutHandler);
            this.socket.on("session_terminated", forceLogoutHandler);
            this.socket.on("kick_user", forceLogoutHandler);

            // ✅ AUTH ERROR
            this.socket.on("auth_error", (data) => {
                console.error("🔐 AUTH ERROR:", data);
                this.handleAuthFailure(data);
                reject(new Error(data.message || "Authentication error"));
            });

            // ✅ CONNECT ERROR
            this.socket.on("connect_error", (error) => {
                console.error("❌ Connection error:", error.message);
                this.isConnected = false;
                reject(error);
            });

            // ✅ DISCONNECT
            this.socket.on("disconnect", (reason) => {
                console.log("🔌 Disconnected:", reason);

                this.isConnected = false;

                this.triggerEvent("disconnected", {
                    reason,
                    isIntentional: this.intentionalDisconnect
                });
            });

            // ✅ GENERIC EVENTS
            this.socket.onAny((event, data) => {
                const skip = [
                    "connect",
                    "disconnect",
                    "connect_error",
                    "auth_error",
                    "force_logout",
                    "online_users_update"
                ];

                if (!skip.includes(event)) {
                    console.log(`📨 Event "${event}":`, data);
                    this.triggerEvent(event, data);
                }
            });

            // ⏱️ TIMEOUT
            setTimeout(() => {
                if (!this.currentUserId) {
                    console.warn("⚠️ Authentication timeout");
                    reject(new Error("Authentication timeout"));
                }
            }, 8000);

        } catch (error) {
            console.error("❌ Socket init error:", error);
            reject(error);
        }
    });
}
    /**
     * Handle authentication failure - ensures WebSocket is disconnected
     */
    handleAuthFailure(data, isForceLogout = false) {
        console.error("🔐 Authentication failure detected:", data);

        this.intentionalDisconnect = true;
        this.isConnected = false;

        // Clear the expiration timer
        if (this.tokenExpiryTimer) {
            clearTimeout(this.tokenExpiryTimer);
            this.tokenExpiryTimer = null;
        }

        this.currentToken = null;
        this.currentUserId = null;

        this.triggerEvent("auth_error", {
            ...data,
            isForceLogout: isForceLogout,
            message: data?.message || (isForceLogout ? "Logged in from another device" : "Invalid or expired token")
        });

        // Also trigger disconnected event specifically for auth failure
        this.triggerEvent("disconnected", { 
            reason: isForceLogout ? "force_logout" : "auth_failure",
            isIntentional: true
        });

        if (this.socket) {
            console.log("🔌 Disconnecting WebSocket due to authentication failure");
            this.socket.removeAllListeners();
            this.socket.disconnect();
            this.socket = null;
        }

        if (this.reconnectAttempts > 0) {
            this.reconnectAttempts = this.maxReconnectAttempts;
        }
    }

    /**
     * Parse raw message format
     */
    parseRawMessage(rawData) {
        const match = rawData.match(/^\[([^\]]+)\]\s+(.+)$/);
        if (match) {
            const eventName = match[1];
            let data = match[2];

            try {
                data = JSON.parse(data);
            } catch (e) {
                // Keep as string if not valid JSON
            }

            return {
                event: eventName,
                data: data,
                raw: rawData
            };
        }

        return { event: "unknown", raw: rawData };
    }

    /**
     * Send custom message through Socket.IO
     */
    send(event, data) {
        if (this.socket && this.socket.connected && !this.intentionalDisconnect) {
            // Check if token is still valid before sending
            if (this.isTokenExpired(this.currentToken)) {
                console.warn("⚠️ Cannot send: Token has expired");
                this.handleTokenExpired();
                return;
            }
            this.socket.emit(event, data);
            console.log(`📤 Sent event "${event}":`, data);
        } else {
            console.warn("⚠️ Cannot send - Socket.IO not connected or auth failed");
        }
    }

    /**
     * Register event handler
     */
    on(event, handler) {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, []);
        }
        this.eventHandlers.get(event).push(handler);
        console.log(`🎯 Registered handler for event: ${event}`);
    }

    /**
     * Remove event handler
     */
    off(event, handler) {
        if (this.eventHandlers.has(event)) {
            const handlers = this.eventHandlers.get(event);
            const index = handlers.indexOf(handler);
            if (index > -1) {
                handlers.splice(index, 1);
            }
        }
    }

    
    triggerEvent(event, data) {
        console.log(`🔔 Triggering event "${event}" with data:`, data);
        if (this.eventHandlers.has(event)) {
            const handlers = this.eventHandlers.get(event);
            handlers.forEach(handler => {
                try {
                    handler(data);
                } catch (error) {
                    console.error(`❌ Error in ${event} handler:`, error);
                }
            });
        }
    }

    /**
     * Disconnect Socket.IO manually
     */
    disconnect() {
        console.log("🔌 Manually disconnecting Socket.IO");
        this.intentionalDisconnect = true;
        this.currentUserId = null;
        this.currentToken = null;

        // Clear the expiration timer
        if (this.tokenExpiryTimer) {
            clearTimeout(this.tokenExpiryTimer);
            this.tokenExpiryTimer = null;
        }

        if (this.socket) {
            this.socket.removeAllListeners();
            this.socket.disconnect();
            this.socket = null;
        }
        this.isConnected = false;
    }

    /**
     * Get connection status
     */
    getConnectionStatus() {
        return {
            isConnected: this.isConnected && this.socket?.connected && !this.intentionalDisconnect,
            socketId: this.socket?.id,
            userId: this.currentUserId,
            reconnectAttempts: this.reconnectAttempts,
            serverUrl: this.serverUrl,
            hasValidToken: !!this.currentToken && !this.isTokenExpired(this.currentToken),
            intentionalDisconnect: this.intentionalDisconnect,
            tokenExpiryTime: this.getTokenExpiryTime(this.currentToken)
        };
    }

    /**
     * Get current user ID
     */
    getCurrentUserId() {
        return this.currentUserId;
    }

    /**
     * Check if token is valid
     */
    isTokenValid() {
        return !!this.currentToken && !this.isTokenExpired(this.currentToken) && !this.intentionalDisconnect && this.isConnected;
    }

    /**
     * Refresh token (call this when you get a new token)
     * @param {string} newToken - New authentication token
     */
    refreshToken(newToken) {
        if (!newToken) {
            console.warn("Cannot refresh token: No new token provided");
            return false;
        }

        if (this.isTokenExpired(newToken)) {
            console.error("Cannot refresh: New token is already expired");
            this.handleTokenExpired();
            return false;
        }

        console.log("🔄 Refreshing WebSocket token");
        this.currentToken = newToken;

        // Reschedule token expiration with new token
        this.scheduleTokenExpiration();

        // Optionally, send token refresh to server
        if (this.socket && this.socket.connected) {
            this.send("refresh_token", { token: newToken });
        }

        return true;
    }
}

// Create singleton instance
const websocketService = new WebSocketService();
export default websocketService;